import React, { useEffect, useState } from "react";
import c15 from "../../../images/c15.png";
import c16 from "../../../images/c16.png";
import c17 from "../../../images/c17.png";
import c18 from "../../../images/c18.png";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { useGetRecentWinnersQuery } from "../../../store/apis";
import { importGameImages } from "../../../utils";
import { GameModes } from "../../../config";
import { CURRENCY } from "../../../constants";

const Winners = () => {
  const [activeTab, setActiveTab] = useState("daily");
  const { data, isLoading } = useGetRecentWinnersQuery();
  const [gameImages, setGameImages] = useState({});
  const [filteredWinners, setFilteredWinners] = useState([]);
  const [availableTabs, setAvailableTabs] = useState([]);

  useEffect(() => {
    if (!data || isLoading) return;

    const categories = ["daily", "weekly", "monthly"];
    const tabsWithWinners = categories.filter((cat) =>
      data.some((winner) => winner.category === cat)
    );

    setAvailableTabs(tabsWithWinners);

    // If current tab has no data, set the first valid one
    if (!tabsWithWinners.includes(activeTab)) {
      setActiveTab(tabsWithWinners[0] || ""); // fallback empty if no data
    }
  }, [activeTab, data, isLoading]);

  // Load game images for all providers
  useEffect(() => {
    const loadImages = async () => {
      try {
        if (data) {
          const providers = [...new Set(data.map((winner) => winner.provider))];

          // Load images for each provider
          const allGameImages = []; // Changed from object to array

          for (const provider of providers) {
            const providerImages = await importGameImages(provider);

            // Since importGameImages now returns an array, spread it into our main array
            allGameImages.push(...providerImages);
          }

          // If you need to convert back to object format for lookup:
          const imagesObj = {};
          allGameImages.forEach((game) => {
            imagesObj[game.alias] = game.imagePath;
          });

          setGameImages(imagesObj); // Keep as object for easy lookup
          // OR if you want to store as array:
          // setGameImages(allGameImages);
        }
      } catch (error) {
        console.error("Error loading game images:", error);
      }
    };

    loadImages();
  }, [data]);

  // Filter and transform winners based on active tab and loaded images
  useEffect(() => {
    if (!data || isLoading) {
      return;
    }

    const categorizedWinners = data.map((winner) => {
      const winnerDate = new Date(winner.createdAt);

      return {
        ...winner,
        date: winnerDate
          .toLocaleDateString("en-GB", {
            day: "numeric",
            month: "numeric",
            year: "numeric",
          })
          .replace(/\//g, "."),
        time: winnerDate.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        imgSrc: getGameImage(winner.game_alias, winner.provider),
      };
    });

    // Count winners by type for logging
    const dailyCount = categorizedWinners.filter(
      (w) => w.category === "daily"
    ).length;
    const weeklyCount = categorizedWinners.filter(
      (w) => w.category === "weekly"
    ).length;
    const monthlyCount = categorizedWinners.filter(
      (w) => w.category === "monthly"
    ).length;

    const filtered = categorizedWinners.filter(
      (winner) => winner.category === activeTab
    );

    setFilteredWinners(filtered);
  }, [data, activeTab, gameImages, isLoading]);

  // Function to get game image based on alias and provider
  const getGameImage = (gameAlias, provider) => {
    // Try to find the image in our loaded images
    const gameKey = gameAlias;
    const providerKey = provider ? provider.toLowerCase() : "default";

    // Check if we have this game in our images
    if (gameImages[gameKey]) {
      return gameImages[gameKey];
    }

    // Return empty string to indicate we should use the color div fallback
    return "";
  };

  // Format currency amounts with commas
  const formatAmount = (amount) => {
    return parseFloat(amount).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  // Mask customer ID for privacy
  const maskId = (id) => {
    if (!id) return "";
    const parts = id.split("-");
    return (
      parts[0].substring(0, 2) + "***" + parts[0].substring(parts[0].length - 2)
    );
  };

  // Show skeleton loader while data is loading
  if (isLoading) {
    return <WinnersSkeleton />;
  }

  return (
    <div className="mb-3 winners-slider mt-3">
      <ul className="mb-3 md:mb-4 flex flex-row justify-center gap-3 md:gap-5">
        {availableTabs.map((tab) => (
          <li
            key={tab}
            className={`text-darkblue dark:text-white hover:text-yellow whitespace-nowrap text-xs md:text-sm font-semibold cursor-pointer ${
              activeTab === tab ? "text-yellow underline" : ""
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "daily"
              ? "Daily Top Winners"
              : tab === "weekly"
              ? "Weekly Winners"
              : "Monthly Winners"}
          </li>
        ))}
      </ul>

      {filteredWinners.length === 0 ? (
        <div className="text-center text-white py-4">
          No {activeTab} winners to display at this time
        </div>
      ) : (
        <Marquee pauseOnHover>
          {filteredWinners.map((winner, index) => (
            <div
              key={index}
              className="rounded-md flex gap-1 bg-white dark:bg-bluedarkmode"
            >
              <Link
                to={`/game/${winner.game_alias}?provider=${winner.provider}&mode=${GameModes.REAL}`}
                className="p-1 pr-0 w-fit"
              >
                {winner.imgSrc ? (
                  <img
                    src={winner.imgSrc}
                    alt={`${winner.game_alias} icon`}
                    className="object-cover w-24 min-w-24 lg:w-32 lg:min-w-32 h-16 lg:h-20"
                  />
                ) : (
                  // Fallback div with background color when image is not available
                  <div className="flex items-center justify-center bg-gray-300 dark:bg-darktitleColor w-24 min-w-24 lg:w-32 lg:min-w-32 h-16 lg:h-20">
                    <span className="text-xs text-darkblue dark:text-white text-center font-medium">
                      {winner.game_alias?.substring(0, 10)}
                    </span>
                  </div>
                )}
              </Link>
              <div className="flex flex-col justify-between w-full p-1 pr-2">
                <div className="flex flex-col">
                  <p className="text-darkblue dark:text-white text-sm lg:text-base font-bold inline-flex line-clamp-1">
                    {winner.won_amount}
                    <span className="inline-block transform text-xs pl-1 mb-auto">
                      {CURRENCY}
                    </span>
                  </p>
                  <p className="text-darkblue dark:text-white text-xs lg:text-sm font-medium line-clamp-1 block">
                    ID {maskId(winner.customer_id)}
                  </p>
                </div>
                <p className="text-darkblue dark:text-white text-xs font-normal line-clamp-1 ml-auto">
                  {winner.date} &nbsp;|&nbsp; {winner.time}
                </p>
              </div>
            </div>
          ))}
        </Marquee>
      )}
    </div>
  );
};

export default Winners;

// Skeleton loader component for winners
const WinnersSkeleton = () => {
  // Create an array of placeholder items
  const skeletonItems = Array(5).fill(0);

  return (
    <div className="mb-3 winners-slider">
      <ul className="mb-3 md:mb-5 flex flex-row justify-center gap-3 sm:gap-5">
        {/* Tab skeleton loaders */}
        <li className="bg-darktitleColor animate-pulse rounded h-5 w-24"></li>
        <li className="bg-darktitleColor animate-pulse rounded h-5 w-24"></li>
        <li className="bg-darktitleColor animate-pulse rounded h-5 w-24"></li>
      </ul>

      <Marquee pauseOnHover>
        {skeletonItems.map((_, index) => (
          <div
            key={index}
            className="rounded-md flex gap-1 bg-forestGreen mx-2"
          >
            <div className="p-1 pr-0 w-fit">
              {/* Game image skeleton */}
              <div className="bg-darktitleColor animate-pulse w-24 min-w-24 lg:w-32 lg:min-w-32 h-16 lg:h-20"></div>
            </div>
            <div className="flex flex-col justify-between w-full p-1 pr-2">
              <div className="flex flex-col">
                {/* Amount skeleton */}
                <div className="bg-darktitleColor animate-pulse h-5 w-20 mb-1 rounded"></div>
                {/* ID skeleton */}
                <div className="bg-darktitleColor animate-pulse h-4 w-16 rounded"></div>
              </div>
              {/* Date/time skeleton */}
              <div className="bg-darktitleColor animate-pulse h-3 w-24 ml-auto rounded"></div>
            </div>
          </div>
        ))}
      </Marquee>
    </div>
  );
};
